import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu';
import { getMenuFromPath, getPathFromSubMenu } from '@/config/routes';

interface MenuItem {
  id: string;
  label: string;
  /** 중간 폭에서 보이는 짧은 이름. 좁은 폭에서는 아이콘만 보인다 (#220) */
  shortLabel: string;
  icon: string;
  children?: SubMenuItem[];
  roles?: string[];
}

interface SubMenuItem {
  id: string;
  label: string;
  roles?: string[];
  isSeparator?: boolean;
  sectionLabel?: string;
}

const TopNavigation: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 현재 URL에서 활성 메뉴 결정
  const menuInfo = getMenuFromPath(location.pathname);
  const activeMenu = menuInfo?.menuId || '';
  const activeSubMenu = menuInfo?.subMenuId || '';

  // 역할별 메뉴 정의 (일반 사용자와 동일한 구조)
  const menuItems: MenuItem[] = [
    {
      id: 'customers',
      label: '이용자 관리',
      shortLabel: '이용자',
      icon: '👥',
      roles: ['saas_admin', 'admin'], // 상위 메뉴도 역할 체크
      children: [
        {
          id: 'user-management',
          label: '이용자 관리',
          roles: ['saas_admin', 'admin'],
        },
        {
          id: 'separator-0',
          label: '',
          isSeparator: true,
        },
        {
          id: 'customer-management',
          label: '고객사 관리',
          roles: ['saas_admin'],
          sectionLabel: '슈퍼관리자 전용',
        },
        {
          id: 'user-management-all',
          label: '전체 이용자 관리',
          roles: ['saas_admin'],
          sectionLabel: '슈퍼관리자 전용',
        },
      ],
    },
    {
      id: 'services',
      label: '서비스 이용',
      shortLabel: '서비스',
      icon: '🚀',
      roles: ['saas_admin', 'admin', 'user'], // 모든 역할 접근 가능
      children: [
        {
          id: 'service-usage',
          label: '서비스 이용',
          roles: ['saas_admin', 'admin', 'user'],
        },
        {
          id: 'separator-1',
          label: '',
          isSeparator: true,
        },
        {
          id: 'service-management',
          label: '서비스 관리',
          roles: ['saas_admin'],
          sectionLabel: '슈퍼관리자 전용',
        },
        {
          id: 'service-credit-plans',
          label: '서비스 요금제 관리',
          roles: ['saas_admin'],
        },
        {
          id: 'login-integrations',
          label: '로그인 연동 현황',
          roles: ['saas_admin'],
        },
        {
          id: 'user-activity',
          label: '이용 · 결제 현황',
          roles: ['saas_admin'],
        },
      ],
    },
    {
      id: 'settings',
      label: '설정',
      shortLabel: '설정',
      icon: '⚙️',
      roles: ['saas_admin', 'admin', 'user'], // 모든 역할 접근 가능
      children: [
        {
          id: 'customer-settings',
          label: '회사 설정',
          roles: ['saas_admin', 'admin'],
        },
        {
          id: 'activity-logs',
          label: '이용자 로그',
          roles: ['saas_admin', 'admin', 'user'],
        },
        {
          id: 'separator-3',
          label: '',
          isSeparator: true,
        },
        {
          id: 'user-logs-all',
          label: '전체 이용자 로그',
          roles: ['saas_admin'],
          sectionLabel: '슈퍼관리자 전용',
        },
        {
          id: 'system-logs',
          label: '시스템 로그',
          roles: ['saas_admin'],
        },
        {
          id: 'environment-settings',
          label: '환경설정',
          roles: ['saas_admin'],
        },
        {
          id: 'email-templates',
          label: '이메일 템플릿 관리',
          roles: ['saas_admin'],
        },
        {
          id: 'ai-monitoring',
          label: 'AI 작업 모니터링',
          roles: ['saas_admin'],
        },
        {
          id: 'ai-provider-settings',
          label: 'AI 제공자 설정',
          roles: ['saas_admin'],
        },
      ],
    },
  ];

  // 현재 역할에 접근 가능한 메뉴 필터링 및 라벨 적용
  const getAccessibleMenus = () => {
    if (!user) return [];
    
    return menuItems
      .filter(menu => {
        // 상위 메뉴의 역할 체크
        if (menu.roles && !menu.roles.includes(user.role)) {
          return false;
        }
        return true;
      })
      .map(menu => ({
        ...menu,
        children: menu.children?.filter(child => 
          !child.roles || child.roles.includes(user.role)
        ),
      }))
      .filter(menu => {
        // 자식 메뉴가 있고, 구분선이 아닌 실제 메뉴 항목이 있는지 확인
        if (!menu.children || menu.children.length === 0) {
          return false;
        }
        // 구분선만 있는 경우 제외
        const hasRealMenuItems = menu.children.some(child => !child.isSeparator);
        return hasRealMenuItems;
      });
  };

  const accessibleMenus = getAccessibleMenus();

  const handleMenuClick = (subMenuId: string) => {
    const path = getPathFromSubMenu(subMenuId);
    navigate(path);
  };

  return (
    <nav className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 폭이 줄면 전체 이름 → 짧은 이름 → 아이콘만으로 좁힌다 (#220) */}
        <div className="flex items-center gap-1 h-14">
          {accessibleMenus.map((menu) => (
            <DropdownMenu key={menu.id}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={activeMenu === menu.id ? "default" : "ghost"}
                  aria-label={menu.label}
                  title={menu.label}
                  className={`flex items-center gap-1 px-2 sm:gap-2 sm:px-3 ${
                    activeMenu === menu.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <span aria-hidden="true">{menu.icon}</span>
                  <span className="hidden md:inline">{menu.label}</span>
                  <span className="hidden sm:inline md:hidden">{menu.shortLabel}</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {menu.children && menu.children.length > 0 ? (
                  menu.children.map((subMenu, index) => {
                    // 구분선 처리: 다음 항목이 슈퍼관리자 전용인지 확인
                    if (subMenu.isSeparator) {
                      // 다음 항목이 슈퍼관리자 전용인지 확인
                      const nextItem = menu.children?.[index + 1];
                      const isNextItemSuperAdminOnly = nextItem && (
                        (nextItem.roles?.includes('saas_admin') && nextItem.roles.length === 1) ||
                        !!nextItem.sectionLabel
                      );
                      
                      // 슈퍼관리자가 아니거나 다음 항목이 슈퍼관리자 전용이 아니면 구분선을 렌더링하지 않음
                      if (user?.role !== 'saas_admin' || !isNextItemSuperAdminOnly) {
                        return null;
                      }
                      
                      return (
                        <DropdownMenuSeparator 
                          key={subMenu.id} 
                          className="bg-purple-200 dark:bg-purple-800 h-0.5 my-1"
                        />
                      );
                    }
                    
                    // 섹션 라벨 표시: 이전 항목이 구분선이고 현재 항목에 섹션 라벨이 있는 경우
                    const prevItem = menu.children?.[index - 1];
                    const showSectionLabel = subMenu.sectionLabel && prevItem?.isSeparator && user?.role === 'saas_admin';
                    
                    // 슈퍼관리자 전용 섹션인지 확인 (구분선 이후의 모든 항목)
                    const isInSuperAdminSection = prevItem?.isSeparator && user?.role === 'saas_admin';
                    const isSuperAdminOnly = subMenu.roles?.includes('saas_admin') && subMenu.roles.length === 1;
                    
                    // 슈퍼관리자 전용 항목인지 확인
                    // - 구분선 이후의 모든 항목 (isInSuperAdminSection)
                    // - 또는 슈퍼관리자 전용 역할만 있는 항목 (isSuperAdminOnly)
                    // - 또는 섹션 라벨이 있는 항목 (sectionLabel이 있으면 슈퍼관리자 전용)
                    const isSuperAdminItem = isInSuperAdminSection || isSuperAdminOnly || !!subMenu.sectionLabel;
                    
                    return (
                      <React.Fragment key={subMenu.id}>
                        {showSectionLabel && (
                          <div className="px-2 py-1.5 text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider bg-purple-50 dark:bg-purple-900/20">
                            {subMenu.sectionLabel}
                          </div>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleMenuClick(subMenu.id)}
                          className={`cursor-pointer ${
                            activeSubMenu === subMenu.id
                              ? 'bg-primary text-primary-foreground'
                              : ''
                          } ${
                            // 슈퍼관리자 전용 항목: 흰 배경에 보라색 글자 (활성화되지 않은 경우)
                            isSuperAdminItem && activeSubMenu !== subMenu.id
                              ? 'text-purple-700 dark:text-purple-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              : ''
                          }`}
                        >
                          {subMenu.label}
                        </DropdownMenuItem>
                      </React.Fragment>
                    );
                  })
                ) : (
                  // 접근 가능한 메뉴가 없을 때 안내 메시지 표시
                  <DropdownMenuItem disabled className="text-gray-400 cursor-not-allowed">
                    이용할 수 있는 메뉴가 없습니다.
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default TopNavigation;

